import React, { RefObject, useEffect, useRef, useState, VideoHTMLAttributes } from 'react'
import { SpaceProps, MaxWidthProps } from 'styled-system'
import { useCombinedRefs } from '@lib/hooks/useCombinedRefs'
import { Icon } from '../Icon'
import Spinner from '../../rolex-ui/VideoPlayer/Spinner'
import * as S from './elements'

type Props = VideoHTMLAttributes<HTMLVideoElement> &
  MaxWidthProps &
  SpaceProps & {
    ratio?: number | number[]
    videoSrc: string
    placeholderSrc: string
    images: string[]
    text?: string
    ref?: RefObject<HTMLVideoElement>
    isFixedRatio?: boolean
    isAutoPlay?: boolean
  }

export const VideoPlayer = React.forwardRef<HTMLVideoElement, Props>(
  (
    {
      maxWidth = '44rem',
      ratio = [0.5625],
      videoSrc,
      placeholderSrc,
      images,
      text,
      isFixedRatio = false,
      isAutoPlay = false,
      ...rest
    },
    ref
  ) => {
    const innerRef = useRef<HTMLVideoElement>(null)
    const combinedRef = useCombinedRefs<HTMLVideoElement>(ref, innerRef)

    const [status, setStatus] = useState<'idle' | 'loading' | 'playing'>('idle')

    // This will initialize the loading state
    const requestVideoPlay = e => {
      if (combinedRef.current) {
        setStatus('loading')
        combinedRef.current.play()
        // Remove focus from the play button to the player
        combinedRef.current.focus()
      }
    }

    useEffect(() => {
      const player = combinedRef.current

      if (!player) return

      const handlePlayEvent = () => {
        setStatus('playing')
      }

      // Listen to the playing event (can be triggered from internal or external play button)
      player.addEventListener('playing', handlePlayEvent)
      return () => {
        player.removeEventListener('playing', handlePlayEvent)
      }
    }, [combinedRef])

    return (
      <S.Container
        ratio={typeof ratio === 'number' ? [ratio] : ratio}
        isFixedRatio={isFixedRatio}
        isPlaying={status === 'playing'}
        maxWidth={maxWidth}
      >
        <S.Image
          placeholderSrc={placeholderSrc}
          sources={images}
          loading="lazy"
          opacity={status === 'playing' ? 0 : 1}
          status={status}
        />
        <S.Video
          status={status}
          src={videoSrc}
          ref={combinedRef}
          onLoadedData={requestVideoPlay}
          preload="none"
          muted
          playsInline
          controls={!isAutoPlay}
          autoPlay={isAutoPlay}
          loop={isAutoPlay}
          {...rest}
        />
        {!isAutoPlay && (
          <S.Content status={status}>
            <S.PlayButton
              onClick={requestVideoPlay}
              status={status}
              tabIndex={-1}
              disabled={status === 'loading'}
            >
              {status === 'loading' ? <Spinner size={40} /> : <Icon icon="play" size={50} />}
            </S.PlayButton>
            {text && <S.Text>{text}</S.Text>}
          </S.Content>
        )}
      </S.Container>
    )
  }
)

export default VideoPlayer
